import { useEffect, useId, useRef, useState } from 'react';
import { clsx } from 'clsx';
import type {
  ClassificationOption,
  ShellApplicationClassificationFilter,
} from '@/lib/applications/application-classification';

interface ApplicationClassificationBarProps {
  readonly options: readonly ClassificationOption[];
  readonly selectedClassification: ShellApplicationClassificationFilter;
  readonly shouldShow: boolean;
  readonly onSelect: (classification: ShellApplicationClassificationFilter) => void;
}

const COLLAPSE_DELAY_MS = 140;

export function ApplicationClassificationBar({
  options,
  selectedClassification,
  shouldShow,
  onSelect,
}: ApplicationClassificationBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const collapseTimerRef = useRef<number | null>(null);
  const panelId = useId();

  useEffect(() => {
    return () => {
      if (collapseTimerRef.current !== null) {
        window.clearTimeout(collapseTimerRef.current);
      }
    };
  }, []);

  if (!shouldShow) {
    return null;
  }

  const cancelCollapse = () => {
    if (collapseTimerRef.current !== null) {
      window.clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  };

  const expand = () => {
    cancelCollapse();
    setIsExpanded(true);
  };

  const scheduleCollapse = () => {
    cancelCollapse();
    collapseTimerRef.current = window.setTimeout(() => {
      setIsExpanded(false);
      collapseTimerRef.current = null;
    }, COLLAPSE_DELAY_MS);
  };

  return (
    <div
      className="border-b"
      style={{
        background: 'linear-gradient(180deg, rgba(244,246,253,0.96) 0%, rgba(255,255,255,0.98) 100%)',
        borderColor: 'var(--color-border)',
      }}
      onMouseEnter={expand}
      onMouseLeave={scheduleCollapse}
      onFocusCapture={expand}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          scheduleCollapse();
        }
      }}
      data-expanded={isExpanded ? 'true' : 'false'}
    >
      <div className="mx-auto max-w-[var(--content-max-width)] px-4">
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={panelId}
          aria-label="Application classifications"
          onClick={() => setIsExpanded((current) => !current)}
          className="group flex h-4 w-full items-center justify-center rounded-sm text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
        >
          <span
            className="mr-2 h-px w-16 bg-slate-300 transition-colors group-hover:bg-[var(--nwm-teal-400)]"
            aria-hidden="true"
          />
          <span className="whitespace-nowrap">OSS</span>
          <span
            className="ml-2 h-px w-16 bg-slate-300 transition-colors group-hover:bg-[var(--nwm-teal-400)]"
            aria-hidden="true"
          />
        </button>

        <div
          id={panelId}
          className="overflow-hidden transition-[max-height,opacity,padding,transform] duration-200 ease-out"
          style={{
            maxHeight: isExpanded ? '5rem' : '0',
            opacity: isExpanded ? 1 : 0,
            paddingBottom: isExpanded ? '0.625rem' : '0',
            transform: `translateY(${isExpanded ? '0' : '-4px'})`,
          }}
        >
          <div
            role="tablist"
            aria-label="Application classifications"
            className="flex gap-2 overflow-x-auto whitespace-nowrap pb-1"
          >
            {options.map((option) => {
              const isActive = option.key === selectedClassification;
              return (
                <button
                  key={option.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  tabIndex={isExpanded || isActive ? 0 : -1}
                  onClick={() => onSelect(option.key)}
                  className={clsx(
                    'rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500',
                    isActive
                      ? 'text-white shadow-sm'
                      : 'bg-white text-slate-600 hover:text-slate-900',
                  )}
                  style={
                    isActive
                      ? {
                          backgroundColor: 'var(--nwm-navy-800)',
                          borderColor: 'transparent',
                        }
                      : {
                          borderColor: 'var(--color-border)',
                        }
                  }
                >
                  <span>{option.label}</span>
                  <span className={clsx('ml-2 text-[10px]', isActive ? 'text-white/70' : 'text-slate-400')}>
                    {option.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
